<template>
    <div class="show">
        <portal to="actions">
            <div class="row">
                <div class="col-12 col-md-6 mb-4 d-flex justify-content-lg-start justify-content-sm-center">
                    <button type="button" class="btn btn-primary" v-on:click="$router.back()">
                        <i class="fa fa-arrow-left mr-2"></i>
                        Back
                    </button>
                </div>

                <div class="col-12 col-md-6 mb-4 d-flex justify-content-lg-end justify-content-sm-between">
                    <a class="btn btn-primary w-100 w-md-auto" :href="`/api/invoices/${invoice.id}/download`">
                        <i class="fa fa-download mr-2"></i>
                        Download
                    </a>
                </div>
            </div>
        </portal>

        <div class="row mb-15">
            <div class="card mt-5 w-100">
                <div class="h5 p-4 border-bottom">Details</div>
                <div class="body p-4">
                    <div class="pb-4 border-bottom">
                        <div class="h6">
                            Invoicing method
                        </div>

                        <div>
                            {{
                                invoice.invoicing_method == 'per_client'
                                    ? 'Amalgamated'
                                    : invoice.invoicing_method == 'per_patient'
                                    ? 'Separate invoice per patient'
                                    : 'Separate invoice per session'
                            }}
                        </div>
                    </div>

                    <div class="py-4 border-bottom">
                        <div class="h6">
                            Invoice generated
                        </div>

                        <div>{{ invoice.created_at | format }}</div>
                    </div>

                    <div class="pt-4">
                        <div class="h6">
                            Due date
                        </div>

                        <div>{{ invoice.due_date | format }}</div>
                    </div>
                </div>
            </div>

            <div class="card mt-10 w-100">
                <div class="h5 p-4 border-bottom row">
                    <div class="col-2 text-left">Item</div>
                    <div class="col-6 text-left">Details</div>
                    <div class="col-2 text-center">Date</div>
                    <div class="col-2 text-right">Amount</div>
                </div>
                <div class="body p-4">
                    <div
                        class="row border-bottom pb-4"
                        v-for="(line, index) in invoice.lines"
                        :key="`invoice-line-${line.id}`"
                        :class="{ 'border-bottom': index < invoice.lines.length - 1, 'pt-4': index > 0 }"
                    >
                        <div class="col-2 text-left">{{ line.linked_item_type | type }}</div>
                        <div class="col-6 text-left">
                            <ul v-if="line.line_type == 'timesheet_session'" style="padding-inline-start: 0px">
                                <li class="p-2 bg-light d-flex justify-content-between align-items-center">
                                    <div class="h6 my-0">Time</div>
                                    <div class="text-muted">
                                        {{ line.session.session_start | hours }} -
                                        {{ line.session.session_end | hours }}
                                    </div>
                                </li>
                                <li class="p-2 d-flex justify-content-between align-items-center">
                                    <div class="h6 my-0">Therapist</div>
                                    <div class="text-muted">
                                        {{ line.session.therapist.first_name }}
                                        {{ line.session.therapist.last_name }}
                                    </div>
                                </li>

                                <li class="p-2 bg-light d-flex justify-content-between align-items-center">
                                    <div class="h6 my-0">Patient</div>
                                    <div class="text-muted">
                                        {{ line.session.patient.first_name }}
                                        {{ line.session.patient.last_name }}
                                    </div>
                                </li>
                                <li class="p-2 d-flex justify-content-between align-items-center">
                                    <div class="h6 my-0">Discipline</div>
                                    <div class="text-muted">
                                        {{ line.session.discipline.name }}
                                    </div>
                                </li>
                                <li class="p-2 bg-light d-flex justify-content-between align-items-center">
                                    <div class="h6 my-0">Type</div>
                                    <div class="text-muted">
                                        {{ line.session.session_type.name }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div class="col-2 text-center">{{ line.session.session_start | date }}</div>
                        <div class="col-2 text-right">{{ line.negative ? '-' : '' }}&pound;{{ line.amount }}</div>
                    </div>

                    <div class="text-right py-4">
                        <div class="mt-0 mb-1" v-if="invoice.invoicing_method == 'per_client'">
                            Outstanding:
                            <span class="h6" :class="{ 'text-danger': !invoice.positive_balance }">
                                {{ invoice.positive_balance ? '' : '-' }}
                                &pound;{{ invoice.balance }}
                            </span>
                        </div>

                        <div class="my-0">
                            Total:
                            <span class="h6">&pound;{{ invoice.amount }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue';
    import moment from 'moment';

    import VueTable from '@components/table';

    import invoicesService from '@api/invoices';

    export default Vue.extend({
        components: {
            VueTable
        },
        data() {
            return {
                invoice: {},
                loading: {
                    invoice: true
                }
            };
        },
        filters: {
            format(date) {
                return moment(date).format('ddd, DD MMM YYYY [at] HH:mm');
            },
            type(line_type) {
                switch (line_type) {
                    case 'timesheet_session':
                        return 'Session';
                    case 'travel_cost':
                        return 'Travel costs';
                    case 'venue_hire':
                        return 'Venue';
                    case 'resource_making':
                        return 'Resource making';
                    case 'direct_payment':
                        return 'Direct payment';
                    case 'deposit':
                        return 'Deposit';
                    default:
                        return 'Other';
                }
            },
            hours(date) {
                return moment(date).format('HH:mm');
            },
            date(date) {
                return moment(date).format('ddd, DD MMM YYYY');
            }
        },
        methods: {
            fetchInvoice() {
                this.loading.invoice = true;

                invoicesService
                    .get(this.$route.params.id)
                    .then((response) => {
                        this.invoice = response.data;
                    })
                    .catch((error) => {
                        window.location.href = `/error/${error.response.status}`;
                    });
            }
        },
        mounted() {
            this.fetchInvoice();
        }
    });
</script>
